/**
 * Display helpers.
 *
 * Everything from the API is UTC ISO-8601. A timezone only applies here, at the
 * edge, and it is the *viewer's* zone - not the browser's guess and not the
 * author's. Someone in Kuala Lumpur reading a thread from London sees their own
 * clock, with the zone named so there is nothing to work out.
 */

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

function safeDate(iso) {
	if (!iso) return null
	const date = new Date(iso)
	return Number.isNaN(date.getTime()) ? null : date
}

/**
 * A timezone we are certain Intl will accept.
 *
 * `Intl.DateTimeFormat` throws a RangeError on an empty or unrecognised zone,
 * and these helpers are called during render - so one bad value takes the
 * whole page down rather than showing one wrong timestamp. A profile that has
 * not loaded yet, a zone the browser has never heard of, or a stored value
 * from an older release are all reachable in practice.
 *
 * Results are cached because this runs on every timestamp on the page.
 */
const ZONE_CACHE = new Map()

function safeZone(timeZone) {
	if (!timeZone) return "UTC"
	const cached = ZONE_CACHE.get(timeZone)
	if (cached !== undefined) return cached

	let resolved = "UTC"
	try {
		new Intl.DateTimeFormat("en-GB", { timeZone }).format(new Date())
		resolved = timeZone
	} catch {
		resolved = "UTC"
	}
	ZONE_CACHE.set(timeZone, resolved)
	return resolved
}

/** True when Intl recognises this zone - for validating a value before saving it. */
export function isKnownTimeZone(timeZone) {
	return Boolean(timeZone) && safeZone(timeZone) === timeZone
}

/** "just now", "4h ago", then falls back to a full date. */
export function relativeTime(iso, timeZone = "UTC") {
	const date = safeDate(iso)
	if (!date) return ""

	const delta = Date.now() - date.getTime()
	if (delta < 0) return "just now"
	if (delta < MINUTE) return "just now"
	if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`
	if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`
	if (delta < 7 * DAY) return `${Math.floor(delta / DAY)}d ago`
	return shortDate(iso, timeZone)
}

/** "22 Sep 2026" */
export function shortDate(iso, timeZone = "UTC") {
	const date = safeDate(iso)
	if (!date) return ""
	return new Intl.DateTimeFormat(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: safeZone(timeZone)
	}).format(date)
}

/**
 * The full, unambiguous stamp: day, month, year, time, zone.
 * "22 Sep 2026, 19:14 BST"
 */
export function fullTimestamp(iso, timeZone = "UTC") {
	const date = safeDate(iso)
	if (!date) return ""
	return new Intl.DateTimeFormat(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZoneName: "short",
		timeZone: safeZone(timeZone)
	}).format(date)
}

/**
 * The same stamp with the offset spelled out, for tooltips and anywhere the
 * abbreviation alone could be read two ways.
 * "22 Sep 2026, 19:14 BST (UTC+1)"
 */
export function fullTimestampWithOffset(iso, timeZone = "UTC") {
	const date = safeDate(iso)
	if (!date) return ""

	const stamp = fullTimestamp(iso, timeZone)
	const offset = zoneOffset(date, timeZone)
	return offset && !stamp.includes(offset) ? `${stamp} (${offset})` : stamp
}

function zoneOffset(date, timeZone) {
	try {
		const parts = new Intl.DateTimeFormat("en-GB", {
			timeZone: safeZone(timeZone),
			timeZoneName: "longOffset"
		}).formatToParts(date)
		return parts.find((part) => part.type === "timeZoneName")?.value || ""
	} catch {
		return ""
	}
}

/** Axis label: "22 Sep". Chart dates are plain YYYY-MM-DD in UTC. */
export function shortDay(isoDate) {
	const date = new Date(`${isoDate}T00:00:00Z`)
	if (Number.isNaN(date.getTime())) return isoDate
	return date.toLocaleDateString(undefined, {
		day: "numeric",
		month: "short",
		timeZone: "UTC"
	})
}

/** What the browser thinks the timezone is — only ever used as a suggestion. */
export function guessTimeZone() {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
	} catch {
		return "UTC"
	}
}

export function zoneLabel(timeZone) {
	if (!timeZone) return ""
	const offset = zoneOffset(new Date(), timeZone)
	const city = String(timeZone).split("/").pop().replace(/_/g, " ")
	return offset ? `${city} · ${offset}` : city
}

export function fileSize(bytes) {
	if (!Number.isFinite(bytes)) return ""
	if (bytes < 0) return "Unlimited"
	if (bytes < 1024) return `${bytes} B`
	const units = ["KB", "MB", "GB", "TB"]
	let value = bytes / 1024
	let unit = 0
	while (value >= 1024 && unit < units.length - 1) {
		value /= 1024
		unit += 1
	}
	return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

export function plural(count, one, many) {
	return `${count} ${count === 1 ? one : (many ?? `${one}s`)}`
}

/**
 * A plan limit with its noun attached: "No teams", "1 team", "10 teams",
 * "Unlimited teams". Written once because the free plan changing from zero to
 * one is exactly the kind of edit that leaves "1 teams" behind on some page
 * nobody thought to look at.
 */
export function countLabel(value, one, many = `${one}s`) {
	if (value === -1) return `Unlimited ${many}`
	if (value === 0) return `No ${many}`
	if (value === 1) return `1 ${one}`
	return `${value} ${many}`
}

export function limitLabel(value, suffix = "") {
	if (value === -1) return "Unlimited"
	return `${value}${suffix ? ` ${suffix}` : ""}`
}

export async function copyText(text) {
	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch {
		// Clipboard API needs a secure context; fall back to the selection trick.
		try {
			const scratch = document.createElement("textarea")
			scratch.value = text
			scratch.setAttribute("readonly", "")
			scratch.style.position = "fixed"
			scratch.style.opacity = "0"
			document.body.appendChild(scratch)
			scratch.select()
			const ok = document.execCommand("copy")
			document.body.removeChild(scratch)
			return ok
		} catch {
			return false
		}
	}
}

export function readFileAsBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onerror = () => reject(new Error(`Could not read ${file.name}`))
		reader.onload = () => {
			const result = String(reader.result || "")
			resolve(result.slice(result.indexOf(",") + 1))
		}
		reader.readAsDataURL(file)
	})
}
